import { useGLTF } from '@react-three/drei';
import { IS_MOBILE } from '../config';

// bodyMemory — a body's GPU memory is GIVEN BACK on a phone when nobody is
// wearing it any more (2026-10-29: the city crashed Safari on entry).
//
// What happened: the ICT-face bodies (migration 426) are ~55 MB each on the GPU
// (two 2048² JPEG textures, which unpack to full RGBA, plus 53 face shapes over
// 15k vertices) where the old ones were ~21 MB, and a body that left range was
// unmounted but never freed: drei's GLB cache held the parsed scene, and its
// textures and geometry stayed on the GPU for the rest of the visit. On a
// desktop that is a fine trade (walking back is instant); on an iPhone every
// resident you ever passed added up until Safari killed the page.
//
// So every AnimatedResident holds its files while it is mounted. When the last
// holder of a file lets go on a phone, after a grace period (walking past and
// straight back should not refetch), the cached scene's textures and geometry
// are disposed and the cache entry dropped: the next mount loads it fresh.
// Desktop keeps today's behaviour (cached for the visit).
const GRACE_MS = 15000;
const holders = new Map();   // url → count
const timers = new Map();    // url → timeout

export function holdBody(urls) {
  for (const u of urls) {
    holders.set(u, (holders.get(u) || 0) + 1);
    if (timers.has(u)) { clearTimeout(timers.get(u)); timers.delete(u); }
  }
}

function free(gltf) {
  gltf?.scene?.traverse((o) => {
    try { o.geometry?.dispose?.(); } catch { /* already gone */ }
    for (const m of [].concat(o.material || [])) {
      if (!m) continue;
      for (const k of Object.keys(m)) { const v = m[k]; if (v && v.isTexture) { try { v.dispose(); } catch { /* gone */ } } }
      try { m.dispose?.(); } catch { /* gone */ }
    }
  });
}

/** @param gltfs the loaded files, in the same order as `urls` */
export function letGoBody(urls, gltfs) {
  urls.forEach((u, i) => {
    const n = (holders.get(u) || 1) - 1;
    if (n > 0) { holders.set(u, n); return; }
    holders.delete(u);
    if (!IS_MOBILE) return;
    const g = gltfs?.[i];
    timers.set(u, setTimeout(() => {
      timers.delete(u);
      if (holders.get(u)) return;          // somebody put it back on meanwhile
      free(g);
      try { useGLTF.clear(u); } catch { /* not cached */ }
    }, GRACE_MS));
  });
}
